import { Button, Divider, Icon } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { useQueueDashboardFavorite } from '../../../../utils/useQueueDashboardFavorite';

import { QueueDashboardFilterControlFavorites } from './components/favorites/queueDashboardFilterControlFavorites';
import { QueueDashboardFilterControlPresets } from './components/presets/queueDashboardFilterControlPresets';
import { QueueDashboardFilterControlQueues } from './components/queues/queueDashboardFilterControlQueues';
import {
  type QueueDashboardFilterControlTab,
  QueueDashboardFilterControlTabs,
} from './components/tabs/queueDashboardFilterControlTabs';
import styles from './queueDashboardFilterControl.module.scss';
import { queueTypePresets } from './utils/queueTypePresets';

export const QueueDashboardFilterControl = memo(
  function QueueDashboardFilterControl(): JSX.Element {
    const { params } = useAppSearchParams('/dashboard/queues');
    const { favorite } = useQueueDashboardFavorite();

    const [tab, setTab] = useState<QueueDashboardFilterControlTab>('favourite');
    const [isOpen, setIsOpen] = useState(false);
    const [keepOpen, setKeepOpen] = useState(false);
    const popoverContent = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (params.type === 'favourite' || params.type === 'custom') {
        setTab(params.type);
      }
    }, [params.type]);

    const handleOpenChange = useCallback(
      (newIsOpen: boolean) => {
        // Prevent closing the popover when `keepOpen` is true, for example when
        // interacting with modals triggered from within the popover.
        if (!newIsOpen && keepOpen) {
          return;
        }

        setIsOpen(newIsOpen);

        if (!newIsOpen && popoverContent.current) {
          popoverContent.current.scrollTop = 0;
        }
      },
      [keepOpen],
    );

    const selectedQueues = useMemo(
      (): string[] =>
        params.type === 'custom' && params.queue ? [params.queue].flat() : [],
      [params],
    );

    const label = useMemo(() => {
      if (params.type === 'custom') {
        return `${selectedQueues.length} ${
          selectedQueues.length === 1 ? 'queue' : 'queues'
        }`;
      }

      if (params.type === 'favourite' && favorite) {
        return favorite.name;
      }

      return queueTypePresets.get(params.type ?? 'agent')?.label ?? '';
    }, [favorite, params.type, selectedQueues.length]);

    return (
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <Button
            variant="secondary"
            icon={
              params.type === 'custom'
                ? 'users'
                : queueTypePresets.get(params.type ?? 'agent')?.icon
            }
            slotEnd={<Icon name="angle-down" />}
          >
            {label}
          </Button>
        </Popover.Trigger>

        <Popover.Content
          className={styles.popover}
          side="bottom"
          align="end"
          sideOffset={parseInt(sizes.spacer.values[200])}
          ref={popoverContent}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <QueueDashboardFilterControlPresets />

          <Divider />

          <QueueDashboardFilterControlTabs tab={tab} onChange={setTab} />

          <Divider />

          {tab === 'favourite' && (
            <QueueDashboardFilterControlFavorites setKeepOpen={setKeepOpen} />
          )}

          {tab === 'custom' && <QueueDashboardFilterControlQueues />}
        </Popover.Content>
      </Popover.Root>
    );
  },
);
